import { useEffect } from "react";

const PORTAL_TITLE = "LastGO Partner";
const ICON_SIZE = 64;
const MARK_RADIUS = 17;

function findIconLink() {
  return document.querySelector<HTMLLinkElement>('link[rel="icon"]');
}

function plainIconHref(link: HTMLLinkElement) {
  if (link.dataset.plainHref === undefined) link.dataset.plainHref = link.getAttribute("href") ?? "";

  return link.dataset.plainHref;
}

function markColor() {
  const style = getComputedStyle(document.documentElement);

  return style.getPropertyValue("--primary").trim() || style.getPropertyValue("--foreground").trim();
}

/** Resolves to null whenever the browser cannot draw the mark — no canvas, an
 *  icon that failed to load — so the tab keeps the icon it already has instead
 *  of losing it. */
function drawMarkedIcon(source: string): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const icon = new Image();
      icon.onerror = () => resolve(null);
      icon.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = ICON_SIZE;
          canvas.height = ICON_SIZE;
          const context = canvas.getContext("2d");
          if (!context) {
            resolve(null);
            return;
          }
          context.drawImage(icon, 0, 0, ICON_SIZE, ICON_SIZE);
          context.beginPath();
          context.arc(ICON_SIZE - MARK_RADIUS, MARK_RADIUS, MARK_RADIUS, 0, 2 * Math.PI);
          context.fillStyle = markColor();
          context.fill();
          resolve(canvas.toDataURL("image/png"));
        } catch {
          resolve(null);
        }
      };
      icon.src = source;
    } catch {
      resolve(null);
    }
  });
}

/** Never animated: a blinking tab reads as a fault rather than as a message.
 *  Both marks are dropped when this layout goes away, so a login page never
 *  carries the last partner's queue. */
export function useAwaitingAnswerSignal(count: number | null) {
  const waiting = count ?? 0;

  useEffect(() => {
    document.title = waiting > 0 ? `(${waiting}) ${PORTAL_TITLE}` : PORTAL_TITLE;

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, [waiting]);

  useEffect(() => {
    const link = findIconLink();
    if (!link) return;

    const plain = plainIconHref(link);
    if (!plain) return;

    const wearPlainIcon = () => link.setAttribute("href", plain);

    if (waiting === 0) {
      wearPlainIcon();
      return;
    }

    let abandoned = false;
    void drawMarkedIcon(plain).then((marked) => {
      if (!abandoned && marked) link.setAttribute("href", marked);
    });

    return () => {
      abandoned = true;
      wearPlainIcon();
    };
  }, [waiting]);
}
